import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface LandingPageVariant {
  id: string;
  variant_key: string;
  layout_name: string;
  traffic_weight: number;
  config: Record<string, unknown>;
}

interface VariantAssignmentResult {
  variantKey: string | null;
  layoutName: string;
  variantConfig: Record<string, unknown>;
  variants: LandingPageVariant[];
  isLoading: boolean;
  setVariantKey: (key: string) => void;
}

const STORAGE_PREFIX = "ab_variant_";

function getStoredVariant(generatorSlug: string): string | null {
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}${generatorSlug}`);
  } catch {
    return null;
  }
}

function storeVariant(generatorSlug: string, variantKey: string) {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${generatorSlug}`, variantKey);
  } catch {
    // localStorage unavailable
  }
}

/**
 * Weighted random selection from a list of variants.
 * Weights are relative — e.g. [50, 50] = 50/50, [70, 30] = 70/30.
 */
function weightedRandom(variants: LandingPageVariant[]): LandingPageVariant {
  const totalWeight = variants.reduce((sum, v) => sum + v.traffic_weight, 0);
  let random = Math.random() * totalWeight;
  for (const variant of variants) {
    random -= variant.traffic_weight;
    if (random <= 0) return variant;
  }
  return variants[variants.length - 1];
}

function getVariantOverride(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  return params.get("variant");
}

/**
 * Hook that assigns a landing page variant for the current generator.
 * - Fetches active variants from DB
 * - Checks localStorage for previously assigned variant
 * - Falls back to weighted random selection
 * - Supports ?variant=X URL override (dev mode only)
 * - Returns "default" layout if no variants are configured
 */
export function useVariantAssignment(
  generatorSlug: string | null,
  isDevMode: boolean
): VariantAssignmentResult {
  const [variants, setVariants] = useState<LandingPageVariant[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<LandingPageVariant | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!generatorSlug) {
      setIsLoading(false);
      return;
    }

    async function fetchAndAssign() {
      setIsLoading(true);
      try {
        // Fetch active variants for this generator
        const { data, error } = await supabase
          .from("landing_page_variants")
          .select("id, variant_key, layout_name, traffic_weight, config, generators!inner(slug)")
          .eq("generators.slug", generatorSlug)
          .eq("is_active", true);

        if (error) {
          console.error("Failed to fetch variants:", error);
          setIsLoading(false);
          return;
        }

        const fetchedVariants: LandingPageVariant[] = (data || []).map((row: any) => ({
          id: row.id,
          variant_key: row.variant_key,
          layout_name: row.layout_name,
          traffic_weight: row.traffic_weight,
          config: (row.config as Record<string, unknown>) || {},
        }));

        setVariants(fetchedVariants);

        // No variants configured → use default layout
        if (fetchedVariants.length === 0) {
          setIsLoading(false);
          return;
        }

        // Check URL override (dev mode only)
        const override = getVariantOverride();
        if (isDevMode && override) {
          const overrideVariant = fetchedVariants.find(v => v.variant_key === override);
          if (overrideVariant) {
            setSelectedVariant(overrideVariant);
            storeVariant(generatorSlug, override);
            setIsLoading(false);
            return;
          }
        }

        // Check localStorage for existing assignment
        // Skip stored variants with zero traffic weight (retired layouts)
        const stored = getStoredVariant(generatorSlug);
        if (stored) {
          const storedVariant = fetchedVariants.find(v => v.variant_key === stored);
          if (storedVariant && storedVariant.traffic_weight > 0) {
            setSelectedVariant(storedVariant);
            setIsLoading(false);
            return;
          }
        }

        // Weighted random assignment
        const chosen = weightedRandom(fetchedVariants);
        setSelectedVariant(chosen);
        storeVariant(generatorSlug, chosen.variant_key);
      } catch (err) {
        console.error("Variant assignment failed:", err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchAndAssign();
  }, [generatorSlug, isDevMode]);

  // Manual variant switch (dev mode toggle)
  const setVariantKey = useCallback((key: string) => {
    const variant = variants.find(v => v.variant_key === key);
    if (variant && generatorSlug) {
      setSelectedVariant(variant);
      storeVariant(generatorSlug, key);
    }
  }, [variants, generatorSlug]);

  return {
    variantKey: selectedVariant?.variant_key ?? null,
    layoutName: selectedVariant?.layout_name ?? "default",
    variantConfig: selectedVariant?.config ?? {},
    variants,
    isLoading,
    setVariantKey,
  };
}
